import React from 'react';
import { Mail, Phone, MapPin } from 'lucide-react';

interface ContactProps {
  language: string;
}

const Contact: React.FC<ContactProps> = ({ language }) => {
  const content = {
    en: {
      title: "Contact Us",
      subtitle: "Ready to enhance your natural beauty? Visit our clinic or reach out with any questions.",
      visit: {
        title: "Visit Our Clinic",
        address: "Address",
        phone: "Phone",
        email: "Email",
        policy: "Booking Policy",
        policyText: "Consultations are free of charge. Please book at least 24 hours in advance.",
        location: "Visit Us",
        locationText: "We're located in Aarhus.\nEasy access via public transportation."
      }
    },
    da: {
      title: "Kontakt Os",
      subtitle: "Klar til at fremhæve din naturlige skønhed? Besøg vores klinik eller kontakt os med spørgsmål.",
      visit: {
        title: "Besøg Vores Klinik",
        address: "Adresse",
        phone: "Telefon",
        email: "Email",
        policy: "Bookingpolitik",
        policyText: "Konsultationer er gratis. Venligst book mindst 24 timer i forvejen.",
        location: "Besøg os",
        locationText: "Vi ligger i Aarhus.\nNem adgang med offentlig transport."
      }
    }
  };
  
  return (
    <section className="py-20 bg-white" id="contact">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 
            className="text-3xl md:text-4xl font-serif font-bold mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {content[language as keyof typeof content].title}
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            {content[language as keyof typeof content].subtitle}
          </p>
        </div>
        
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 gap-8">
            <div className="bg-white rounded-lg shadow-md p-6 md:p-8 hover:shadow-lg transition-shadow duration-300 border-l-4 border-[#A68B7C] max-w-2xl mx-auto w-full">
              <h3
                className="text-2xl font-serif font-semibold mb-2"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                {content[language as keyof typeof content].visit.title}
              </h3>
              <p className="text-sm text-[#A68B7C] font-medium mb-6 uppercase tracking-wide">{language === 'da' ? 'Lokation' : 'Location'}</p>

              <div className="space-y-4">
                <div className="flex items-start group">
                  <MapPin size={20} className="mr-3 mt-1 text-[#A68B7C] flex-shrink-0 transition-transform duration-300 group-hover:scale-110" />
                  <div>
                    <h4 className="font-medium mb-1">{content[language as keyof typeof content].visit.address}</h4>
                    <p className="text-gray-600">
                      Thorvaldsensgade 13,<br />
                      8000 Aarhus
                    </p>
                  </div>
                </div>

                <div className="flex items-start group">
                  <Phone size={20} className="mr-3 mt-1 text-[#A68B7C] flex-shrink-0 transition-transform duration-300 group-hover:scale-110" />
                  <div>
                    <h4 className="font-medium mb-1">{content[language as keyof typeof content].visit.phone}</h4>
                    <p className="text-gray-600">+45 42 31 67 54</p>
                  </div>
                </div>

                <div className="flex items-start group">
                  <Mail size={20} className="mr-3 mt-1 text-[#A68B7C] flex-shrink-0 transition-transform duration-300 group-hover:scale-110" />
                  <div>
                    <h4 className="font-medium mb-1">{content[language as keyof typeof content].visit.email}</h4>
                    <p className="text-gray-600">abaseensle.as@gmail.com</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Additional Information Section */}
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-8">
            
            <div className="bg-white rounded-lg shadow-md p-6 md:p-8 hover:shadow-lg transition-shadow duration-300 border-t-4 border-[#A68B7C]">
              <div className="space-y-6">
                <div>
                  <h4 
                    className="text-xl font-serif font-semibold mb-3"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    {content[language as keyof typeof content].visit.policy}
                  </h4>
                  <p className="text-gray-600">
                    {content[language as keyof typeof content].visit.policyText}
                  </p>
                </div>
                
                <div>
                  <h4 
                    className="text-xl font-serif font-semibold mb-3"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    {content[language as keyof typeof content].visit.location}
                  </h4>
                  <p className="text-gray-600 whitespace-pre-line">
                    {content[language as keyof typeof content].visit.locationText}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6 md:p-8 hover:shadow-lg transition-shadow duration-300 border-t-4 border-[#A68B7C]">
              <div className="space-y-6">
                <div>
                  <h4 
                    className="text-xl font-serif font-semibold mb-3"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    {language === 'da' ? 'Transport' : 'Transportation'}
                  </h4>
                  <p className="text-gray-600">
                    {language === 'da'
                      ? 'Lokationen har nem adgang med offentlig transport og parkeringsmuligheder i nærheden.'
                      : 'The location has easy access via public transportation and nearby parking options.'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;